/**
 * SEARCH-002 §3.3 / §5 — Registre extensible des catégories de la palette ⌘K.
 * Extrait de `searchCategories.ts` (hub), qui le ré-exporte.
 *
 * Icônes MONOCHROMES (text-muted-foreground) : le domaine se lit via la FORME
 * de l'icône + l'en-tête de groupe, jamais via une couleur (§2.2).
 */

import {
    BadgeEuro,
    Building2,
    Contact as ContactIcon,
    GraduationCap,
    Presentation,
    Users,
    MapPin,
    UtensilsCrossed,
    BedDouble,
    BookOpen,
    Package,
    Euro,
    ClipboardCheck,
    FileText,
    ClipboardList,
    ShieldAlert,
    ListChecks,
    MessageSquareHeart,
    FileQuestion,
    MonitorPlay,
    Boxes,
    Gavel,
} from 'lucide-react'
import {
    type CategoryConfig,
    type SourceType,
    listingRoute,
    originRoute,
} from './searchCategoriesTypes'

export const CATEGORY_REGISTRY: Record<SourceType, CategoryConfig> = {
    client: {
        icon: Building2,
        route: (h) => `/clients/${h.id}`,
        i18nKey: 'client',
        permission: 'clients:read',
        // Miroir de TYPE_PERMISSION_ALT (registry.py) : sans ces slugs, un
        // commercial « mes fiches » ou un chef des ventes « mon équipe » ne
        // demandait AUCUN client à la recherche.
        permissionAlt: ['clients:own_only', 'clients:read_team'],
        order: 1,
        previewKind: 'generic',
        listing: { toRoute: listingRoute('/clients') },
    },
    contact: {
        icon: ContactIcon,
        // DEEP-LINK : pas de page de détail dédiée → fiche client parente, onglet
        // **Contacts** (`#contacts`) + `?contact=<id>` qui auto-ouvre la fiche du
        // contact (modale d'édition). Repli annuaire si l'organisme est inconnu.
        route: (h) =>
            h.organizationId
                ? `/clients/${h.organizationId}?contact=${h.id}#contacts`
                : '/directory',
        i18nKey: 'contact',
        permission: 'contacts:read',
        order: 2,
        previewKind: 'generic',
        listing: null,
    },
    learner: {
        icon: GraduationCap,
        route: (h) => `/learners/${h.id}`,
        i18nKey: 'learner',
        permission: 'learners:read',
        order: 3,
        previewKind: 'generic',
        listing: { toRoute: listingRoute('/learners') },
    },
    user: {
        icon: Users,
        // DEEP-LINK : ouvre directement la fiche utilisateur (`?user=<id>` →
        // `UsersList openUserId`), pas seulement la liste.
        route: (h) => `/settings/users?user=${h.id}`,
        i18nKey: 'user',
        permission: 'users:read',
        order: 4,
        previewKind: 'generic',
        listing: null,
    },
    instructor: {
        icon: Presentation,
        route: (h) => `/instructors/${h.id}`,
        i18nKey: 'instructor',
        permission: 'instructors:read',
        order: 4.5,
        previewKind: 'generic',
        listing: { toRoute: listingRoute('/instructors') },
    },
    quote: {
        // `BadgeEuro` (€ en pastille) — pièce commerciale, monnaie EUROPÉENNE.
        //
        // ⚠⚠ NE PAS revenir à `Receipt` : son tracé contient un `$`
        // (`M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8` + `M12 17V7` = le S barré
        // du dollar). Convention produit : une icône finance porte l'euro ou
        // reste neutre, JAMAIS le dollar (retour PO 2026-07-09, re-signalé le
        // 2026-08-02 — la spec SALE-019 §7.1 prescrivait `Receipt` à tort).
        // `ReceiptEuro` / `ReceiptText` n'existent pas en lucide-react 0.303.0.
        // `Euro` seul est déjà pris par les Frais (`expense`) : `BadgeEuro`
        // porte le même symbole sans créer d'ambiguïté entre les deux.
        icon: BadgeEuro,
        route: (h) => `/sales/quotes/${h.id}`,
        i18nKey: 'quote',
        permission: 'quotes:read',
        permissionAlt: ['quotes:read_own_only', 'quotes:read_team'],
        order: 4.7,
        previewKind: 'generic',
        listing: { toRoute: listingRoute('/sales/quotes') },
    },
    // 40_TENDER (mig 653) — l'appel d'offres, juste après le devis : c'est la même
    // étape du cycle commercial, vue depuis l'acheteur public.
    //
    // `listing: null` — la liste des AO lit `?search=`, pas `?q=` (cf.
    // `TendersListPage`, `useState(searchParams.get('search'))`). Un « Voir tout »
    // construit par `listingRoute` y ouvrirait un écran NON filtré : une promesse
    // fausse, exactement ce que la note des ajouts 2026-08-09 dit d'éviter.
    tender: {
        // `Gavel` (le marteau d'adjudication) — l'attribution d'un marché. Aucune
        // autre catégorie ne le porte, et il ne se confond avec aucune icône
        // finance (`BadgeEuro` du devis, `Euro` des frais).
        icon: Gavel,
        route: (h) => `/sales/tenders/${h.id}`,
        i18nKey: 'tender',
        permission: 'tenders:read',
        order: 4.8,
        previewKind: 'generic',
        listing: null,
    },
    location: {
        icon: MapPin,
        route: (h) => `/logistics/locations/${h.id}`,
        i18nKey: 'location',
        permission: 'locations:read',
        order: 5,
        previewKind: 'generic',
        listing: null,
    },
    catering: {
        icon: UtensilsCrossed,
        route: (h) => `/logistics/catering/${h.id}`,
        i18nKey: 'catering',
        permission: 'catering:read',
        order: 6,
        previewKind: 'generic',
        listing: null,
    },
    accommodation: {
        icon: BedDouble,
        route: (h) => `/logistics/accommodation/${h.id}`,
        i18nKey: 'accommodation',
        permission: 'accommodation:read',
        order: 7,
        previewKind: 'generic',
        listing: null,
    },
    // 30_LOG / RSC — matériel de formation. `order: 7.5` et non 5.5 : la place
    // utile est à la SUITE du bloc logistique (lieux 5, restauration 6,
    // hébergements 7), pas au milieu. Un utilisateur qui cherche « où est mon
    // chariot » lit ce bloc d'un trait.
    // Contrairement à ses trois voisines, cette catégorie porte un `listing` :
    // la liste du parc est une DataTable serveur, et « Voir tout » y mène.
    resource: {
        icon: Boxes,
        route: (h) => `/logistics/resources/${h.id}`,
        i18nKey: 'resource',
        permission: 'resources:read',
        order: 7.5,
        previewKind: 'generic',
        listing: { toRoute: listingRoute('/logistics/resources') },
    },
    formation: {
        icon: BookOpen,
        route: (h) => `/catalog/formations/${h.id}`,
        i18nKey: 'formation',
        permission: 'catalog:read',
        order: 8,
        previewKind: 'generic',
        listing: { toRoute: listingRoute('/catalog/formations') },
    },
    article: {
        icon: Package,
        route: (h) => `/catalog/articles/${h.id}`,
        i18nKey: 'article',
        permission: 'catalog:read',
        order: 9,
        previewKind: 'generic',
        listing: null,
    },
    expense: {
        icon: Euro,
        route: (h) => `/catalog/frais/${h.id}`,
        i18nKey: 'expense',
        permission: 'catalog:read',
        order: 10,
        previewKind: 'generic',
        listing: null,
    },
    assessment: {
        icon: ClipboardCheck,
        route: () => '/pedagogy/assessments',
        i18nKey: 'assessment',
        permission: 'assessment:read',
        order: 11,
        previewKind: 'generic',
        listing: null,
    },
    document: {
        icon: FileText,
        // Routage §6.11 : si le document a un objet d'origine routable, on ouvre
        // CET objet (jamais le PDF figé) ; sinon (libre) on ouvre le coffre sur
        // le fichier. `?file=` permet au coffre de présélectionner le fichier.
        route: (h) =>
            originRoute(h.originType, h.originId, h.organizationId) ?? `/settings/ged?file=${h.id}`,
        i18nKey: 'document',
        permission: 'ged:view',
        order: 12,
        previewKind: 'document',
        listing: null,
    },
    needs_analysis: {
        icon: ClipboardList,
        // Ouvre la fiche client → onglet Activités → ADB pré-ouverte (§6.11).
        route: (h) =>
            h.organizationId ? `/clients/${h.organizationId}?adb=${h.id}#activities` : '/clients',
        i18nKey: 'needs_analysis',
        permission: 'client_needs_analyses:read',
        order: 13,
        previewKind: 'generic',
        listing: null,
    },
    // ---- Ajouts 2026-08-09 (mig 433) ---------------------------------------
    // `listing: null` pour les cinq : aucune de ces listes ne lit `?q=` — poser
    // un « Voir tout » ouvrirait un écran NON filtré, donc une promesse fausse.
    non_conformity: {
        icon: ShieldAlert,
        route: (h) => `/quality/non-conformities/${h.id}`,
        i18nKey: 'non_conformity',
        permission: 'quality:read',
        order: 14,
        previewKind: 'generic',
        listing: null,
    },
    quality_action: {
        icon: ListChecks,
        route: (h) => `/quality/actions/${h.id}`,
        i18nKey: 'quality_action',
        permission: 'quality:read',
        order: 15,
        previewKind: 'generic',
        listing: null,
    },
    satisfaction_response: {
        icon: MessageSquareHeart,
        // Pas de page dédiée : la réponse s'ouvre en modale sur l'onglet
        // « Réponses » via `?response=` (cf. `AnswersAnalysisTab`, DEEP_LINK_RESPONSE).
        route: (h) => `/quality/satisfaction?response=${h.id}#reponses`,
        i18nKey: 'satisfaction_response',
        permission: 'satisfaction:view_responses',
        order: 16,
        previewKind: 'generic',
        listing: null,
    },
    satisfaction_questionnaire: {
        icon: FileQuestion,
        route: (h) => `/quality/satisfaction/questionnaires/${h.id}`,
        i18nKey: 'satisfaction_questionnaire',
        permission: 'satisfaction:read',
        order: 17,
        previewKind: 'generic',
        listing: null,
    },
    elearning_module: {
        icon: MonitorPlay,
        route: (h) => `/pedagogy/elearning/${h.id}`,
        i18nKey: 'elearning_module',
        permission: 'elearning:read',
        order: 18,
        previewKind: 'generic',
        listing: null,
    },
}

/** Liste ordonnée des `source_type` (ordre stable d'affichage). */
export const ORDERED_SOURCE_TYPES: SourceType[] = (
    Object.keys(CATEGORY_REGISTRY) as SourceType[]
).sort((a, b) => CATEGORY_REGISTRY[a].order - CATEGORY_REGISTRY[b].order)
